import { CacheConfig } from "../utilities/cachingConfig";
import { getData, postData } from "./common.service";
import { buildFinalFieldDataFilter } from "../utilities/timeRange";

export async function searchDevices(
  text: string,
  skip: number = 0,
  limit: number = 10
) {
  const qs = {
    where: {
      or: [
        { imei: { like: `.*${text}.*`, options: "i" } },
        { deviceId: { like: `.*${text}.*`, options: "i" } },
      ],
      imei: { exists: true }
    },
    limit: limit,
    skip: skip,
  };

  return getData("/devices?filter=" + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then((res) => res.json());
}

export async function getDeviceGraphs(text: string, timestamp: Date, endTimestamp?: Date) {
  // New bounded form: gte + lte + limit. Legacy single-arg form (gte only)
  // is preserved when endTimestamp is omitted.
  const qs = endTimestamp
    ? buildFinalFieldDataFilter(text, {
        mode: "custom",
        sinceHours: 0,
        startTime: timestamp instanceof Date ? timestamp.toISOString() : String(timestamp),
        endTime: endTimestamp instanceof Date ? endTimestamp.toISOString() : String(endTimestamp),
      })
    : {
        where: {
          and: [
            { timestamp: { gte: timestamp } },
            { deviceId: text }
          ]
        },
        order: "timestamp ASC"
      };

  return getData("/final-field-data?filter=" + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then(async (res) => {
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error(data?.error?.message || `Failed to fetch device graphs (${res.status})`);
    }
    return data;
  });

}

export async function sendCalibrationData(deviceId: string, calibrationData: object) {
  const body = {
    calibration: calibrationData,
  };
  const url = `/devices/${deviceId}`;

  return postData(url, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    usePatchMethod: true,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body
  })
    .then((res) => {
      if (res.ok) {
        return { success: true, res };
      } else {
        return { success: false, message: 'Failed to update calibration' };
      }
    })
    .catch((err) => {
      console.error('Error:', err);
      return { success: false, message: 'An error occurred' };
    });
}

export async function startDeviceMotorTest(deviceId: string) {
  const url = `/devices/${deviceId}/motor-test-commands`;

  return postData(url, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: {}
  })
    .then(async (res) => {
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        return { success: false, message: data?.error?.message || 'Failed to start motor test' };
      }
      return { success: true, data };
    })
    .catch((err) => {
      console.error('Error:', err);
      return { success: false, message: 'An error occurred' };
    });
}

export async function getDeviceMotorTestHistory(deviceId: string, skip: number = 0, limit: number = 20) {
  return getData(`/devices/${deviceId}/motor-test-commands?skip=${skip}&limit=${limit}`, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then((res) => res.json());
}

export async function updateDeviceDetails(deviceId: string, data: any) {
  const body = {
    ccid: data.ccid ?? '',
  };
  const url = `/devices/${deviceId}`;

  return postData(url, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    usePatchMethod: true,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body
  })
    .then((res) => {
      if (res.ok) {
        return { success: true, message: 'Device updated successfully' };
      } else {
        return { success: false, message: 'Failed to update device' };
      }
    })
    .catch((err) => {
      console.error('Error:', err);
      return { success: false, message: 'An error occurred' };
    });
}

export async function updateSensorId(deviceId: string, sensorId: string, imei: string) {
  // First API call: PUT to update sensorId in device
  const deviceUpdateBody = {
    sensorId: sensorId,
  };
  const deviceUrl = `/devices/${deviceId}`;

  try {
    const deviceUpdateResponse = await postData(deviceUrl, {
      basePath: process.env.REACT_APP_CORE_API_BASEPATH,
      sendTokenInHeader: true,
      usePatchMethod: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: deviceUpdateBody
    });

    if (!deviceUpdateResponse.ok) {
      return { success: false, message: 'Failed to update device sensorId' };
    }

    // Second API call: POST to OTA requests
    const otaRequestBody = [{
      data: { sensorId: sensorId },
      imei: imei
    }];

    const otaResponse = await postData('/ota-requests', {
      basePath: process.env.REACT_APP_CORE_API_BASEPATH,
      sendTokenInHeader: true,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: otaRequestBody
    });

    if (!otaResponse.ok) {
      return { success: false, message: 'Device updated but OTA request failed' };
    }

    return { success: true, message: 'SensorId updated successfully and OTA request sent' };
  } catch (err) {
    console.error('Error updating sensorId:', err);
    return { success: false, message: 'An error occurred while updating sensorId' };
  }
}

export async function activateSIM(imei: string) {
  const url = `/devices/${imei}/activate`;
  return getData(url, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  });
}

export async function getSIMDetails(imei: string) {
  const url = `/devices/${imei}/sim-details`;
  return getData(url, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  });
}

export async function suspendSIM(imei: string) {
  const url = `/devices/${imei}/suspend`;
  return getData(url, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  });
}

export async function getInventoryLocationsPaginated(text: string = "", skip: number = 0, limit: number = 20) {
  const qs = {
    where: {
      and: [
        {
          name: { like: `.*${text}.*`, options: 'i' }
        },
        {
          name: { nlike: `.*Fyllo Warehouse.*`, options: 'i' }
        }
      ]

    },
    limit: limit,
    skip: skip,
    fields: {
      id: true,
      name: true,
      stock: true
    }
  };
  return getData('/inventory-locations?filter=' + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then(data => data.json())
}

export async function getInventoryLocationById(text: string = "") {
  const qs = {
    where: {
      id: `${text}`
    },
    fields: {
      id: true,
      stock: true
    }
  };
  return getData('/inventory-locations?filter=' + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then(data => data.json())
}

export async function getInventoryItemsPaginated(text: string = "", skip: number = 0, limit: number = 20) {
  const qs = {
    where: {
      name: {
        inq: ['nero_single',
          'nero_double',
          'kairo',
          'nero_single_rental',
          'nero_double_rental',
          'kairo_rental',
          'nero_infinity_single',
          'nero_infinity_double',
          'nero_pulse',
          'nero_pulse_lite',
          'zuno',
          'myko',
          'kairo_infinity',
          'irrigation_unit',
          'weather_unit',
          'auro'
        ]
      }
    },
    limit: limit,
    skip: skip,
    order: ['name ASC'],
    fields: {
      id: true,
      name: true
    }
  };
  return getData('/inventory-items?filter=' + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then(data => data.json())
}

export async function getInventoryItemsOfLocation(itemIds: string[]) {
  const qs = {
    where: {
      name: {
        inq: ['nero_single',
          'nero_double',
          'kairo',
          'nero_single_rental',
          'nero_double_rental',
          'kairo_rental',
          'nero_infinity_single',
          'nero_infinity_double',
          'nero_pulse',
          'nero_pulse_lite',
          'zuno',
          'myko',
          'kairo_infinity',
          'irrigation_unit',
          'weather_unit',
          'auro'
        ]
      },
      id: { inq: itemIds }
    },
    order: ['name ASC'],
    fields: {
      id: true,
      name: true
    }
  };
  return getData('/inventory-items?filter=' + JSON.stringify(qs), {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then(data => data.json())
}

export async function getInventoryItemById(id: string) {

  const url = `/inventory-items/${id}`;
  return getData(url, {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
  }).then(data => data.json())

}
export async function deductInventory(data: {
  inventoryLocation: any, inventoryItem: any,
  count: number, farmUserId: string, plotId: string, imei: string
}) {
  return postData('/inventory-movements', {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: {
      date: new Date().toISOString(),
      quantity: data.count,
      status: "COMPLETED",
      comments: "from support app",
      inventoryItemId: data.inventoryItem.id,
      fromInventoryLocationId: data.inventoryLocation.id,
      farmUserId: data.farmUserId,
      plotId: data.plotId,
      imei: [data.imei]
    }
  });
}

export async function deductInventoryRental(data: {
  inventoryLocation: any, inventoryItem: any,
  count: number, farmUserId: string, plotId: string, imei: string
}) {
  const subtractedFromMainStock = await postData('/inventory-movements', {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: {
      date: new Date().toISOString(),
      quantity: data.count,
      type: "CONVERTED_TO_RENTAL",
      status: "COMPLETED",
      comments: "reduced from main stock from support app",
      inventoryItemId: data.inventoryItem.id,
      fromInventoryLocationId: data.inventoryLocation.id
    }
  });
  const addedAsRentalStock = await postData('/inventory-movements', {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: {
      date: new Date().toISOString(),
      quantity: data.count,
      type: "CONVERTED_TO_RENTAL",
      status: "COMPLETED",
      comments: "added to rental stock from support app",
      inventoryItemId: data.inventoryItem.id + '_rental',
      toInventoryLocationId: data.inventoryLocation.id
    }
  });
  return postData('/inventory-movements', {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: {
      date: new Date().toISOString(),
      quantity: data.count,
      status: "COMPLETED",
      comments: "from support app",
      inventoryItemId: data.inventoryItem.id + '_rental',
      fromInventoryLocationId: data.inventoryLocation.id,
      farmUserId: data.farmUserId,
      plotId: data.plotId,
      imei: [data.imei]
    }
  });
}

export async function addBackToInventory(data: {
  inventoryLocation: any, inventoryItem: any,
  count: number, farmUserId: string, plotId: string, imei: string
}) {
  return postData('/inventory-movements', {
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    sendTokenInHeader: true,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: {
      date: new Date().toISOString(),
      quantity: data.count,
      type: "RETURNED_TO_INVENTORY",
      status: "COMPLETED",
      comments: "returned from support app",
      inventoryItemId: data.inventoryItem.id,
      toInventoryLocationId: data.inventoryLocation.id,
      farmUserId: data.farmUserId,
      plotId: data.plotId,
      imei: [data.imei]
    }
  });
}

export async function postInventoryMovement(payload: any) {
  console.log("recieved: ", payload);
  const url = '/inventory-movements';
  return postData(url, {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    body: {
      date: new Date().toISOString(),
      quantity: payload.quantity,
      status: "OPEN",
      comments: payload.comments,
      inventoryItemId: payload.inventoryItemId,
      toInventoryLocationId: payload.toInventoryLocationId,
      fromInventoryLocationId: payload.fromInventoryLocationId,
      userId: payload.userId,
      type: payload.type
    }
  })
}

export async function completeMovement(id: string) {
  const url = `/inventory-movements/${id}/complete`;
  return getData(url, {
    sendTokenInHeader: true,
    basePath: process.env.REACT_APP_CORE_API_BASEPATH,
  })
}

export async function getDeviceTypes() {
  return getData(
    "/devices/types",
    {
      basePath: process.env.REACT_APP_CORE_API_BASEPATH,
      sendTokenInHeader: true,
    },
    CacheConfig.DEVICETYPES.cacheKey,
    CacheConfig.DEVICETYPES.cacheTime
  ).then((data) => data?.map((i: string) => { return { deviceId: i, deviceName: i } }));
}